import React, { useState } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    LinearProgress,
    IconButton,
    TextField,
    Tooltip,
    CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { formatCurrency } from '../../utils/formatters';
import { useObraOrcamento } from '../../hooks/useObraOrcamento';
import { Orcamento } from '../../services/types';

interface ObraOrcamentoTabProps {
    obraId: number;
}

export const ObraOrcamentoTab: React.FC<ObraOrcamentoTabProps> = ({ obraId }) => {
    const { orcamento, loadingOrcamento, errorOrcamento, updateOrcamento } = useObraOrcamento(String(obraId));
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editValue, setEditValue] = useState<string>('');

    if (loadingOrcamento) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
    }

    if (errorOrcamento) {
        return <Typography color="error">Erro ao carregar orçamento: {(errorOrcamento as Error).message}</Typography>;
    }

    const handleEditClick = (item: Orcamento) => {
        setEditingId(item.categoria_id);
        setEditValue(item.valor_previsto.toString());
    };

    const handleCancelClick = () => {
        setEditingId(null);
        setEditValue('');
    };

    const handleSaveClick = async (categoriaId: number) => {
        try {
            const valor = parseFloat(editValue);
            if (isNaN(valor)) return;

            await updateOrcamento({
                obra_id: obraId,
                categoria_id: categoriaId,
                valor_previsto: valor
            });
            setEditingId(null);
        } catch (error) {
            console.error("Erro ao salvar orçamento:", error);
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Orçamento vs. Realizado</Typography>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Categoria</TableCell>
                            <TableCell align="right">Orçamento Previsto</TableCell>
                            <TableCell align="right">Realizado</TableCell>
                            <TableCell align="right">Saldo</TableCell>
                            <TableCell align="center">% Utilizado</TableCell>
                            <TableCell align="center">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {orcamento?.map((item) => (
                            <TableRow key={item.categoria_id}>
                                <TableCell>{item.categoria_nome}</TableCell>
                                <TableCell align="right">
                                    {editingId === item.categoria_id ? (
                                        <TextField
                                            size="small"
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            type="number"
                                            autoFocus
                                        />
                                    ) : (
                                        formatCurrency(item.valor_previsto)
                                    )}
                                </TableCell>
                                <TableCell align="right">{formatCurrency(item.valor_realizado)}</TableCell>
                                <TableCell align="right" sx={{ color: item.saldo < 0 ? 'error.main' : 'success.main', fontWeight: 'bold' }}>
                                    {formatCurrency(item.saldo)}
                                </TableCell>
                                <TableCell align="center" sx={{ width: '20%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{ width: '100%', mr: 1 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={Math.min(item.percentual_utilizado, 100)}
                                                color={item.percentual_utilizado > 100 ? 'error' : 'primary'}
                                                sx={{ height: 10, borderRadius: 5 }}
                                            />
                                        </Box>
                                        <Box sx={{ minWidth: 35 }}>
                                            <Typography variant="body2" color="text.secondary">{`${item.percentual_utilizado}%`}</Typography>
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell align="center">
                                    {editingId === item.categoria_id ? (
                                        <>
                                            <IconButton onClick={() => handleSaveClick(item.categoria_id)} color="primary" size="small">
                                                <SaveIcon />
                                            </IconButton>
                                            <IconButton onClick={handleCancelClick} color="error" size="small">
                                                <CancelIcon />
                                            </IconButton>
                                        </>
                                    ) : (
                                        <Tooltip title="Editar Orçamento">
                                            <IconButton onClick={() => handleEditClick(item)} size="small">
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                        {orcamento?.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">Nenhuma categoria encontrada.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};
